<template>
    <form class="space-y-5 relative" novalidate @submit.prevent="submitCheck">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="space-y-1.5">
                <label class="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1" for="how-it-works-validity-country">
                    {{ labelCountry }}
                </label>
                <div id="how-it-works-validity-country" class="how-it-works-validity-country">
                    <DropdownField
                        :field="vehicleCountryFieldForRender"
                        :has-error="validityErrors.country"
                        :step-data="stepData"
                        :compact="true"
                        :angle-down-solid="angleDownSolid"
                        :countries="countries"
                        @field-change="onCountryFieldChange"
                    />
                </div>
            </div>
            <div class="space-y-1.5">
                <label class="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1" for="how-it-works-validity-plate">
                    {{ labelPlate }}
                </label>
                <div class="how-it-works-validity-plate">
                    <TextInput
                        v-if="licensePlateFieldForRender"
                        :field="licensePlateFieldForRender"
                        :step-data="stepData"
                        :has-error="validityErrors.plate"
                        :countries="countries"
                        :image-src="imageSrc"
                        attach-left
                        @clear-error="clearPlateError"
                    />
                </div>
            </div>
        </div>
        <input
            v-model="validityHp"
            type="text"
            name="validity_hp"
            class="absolute opacity-0 pointer-events-none h-0 w-0"
            tabindex="-1"
            autocomplete="off"
            aria-hidden="true"
        />
        <button
            type="button"
            class="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-hu-green text-white px-10 py-5 rounded-2xl font-black hover:bg-hu-green-dark transition-all shadow-lg uppercase tracking-widest text-sm disabled:opacity-70 disabled:pointer-events-none"
            :disabled="submitting"
            @click="submitCheck"
        >
            {{ buttonLabel }}
            <span class="material-symbols-outlined text-lg" :class="{ 'animate-spin': submitting }">search</span>
        </button>
    </form>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import DropdownField from './BuyVignette/components/DropDownField.vue';
import TextInput from './BuyVignette/components/TextInput.vue';
import { LICENSE_PLATES, LICENSE_PLATE_FLAG_ISO2 } from './BuyVignette/config/licensePlates.js';
import { filterLicensePlateInput } from '../shared/licensePlateInput.js';
import { resolveDefaultVehicleCountryCode } from '../utils/languageDefaultCountry.js';

const VALIDITY_CHECK_SESSION_KEY = 'evignettehungary_validity_check_last';
const VIGNETTE_ACTIVE_SESSION_KEY = 'evignettehungary_vignette_active';

const props = defineProps({
    labelCountry: { type: String, default: '' },
    labelPlate: { type: String, default: '' },
    platePlaceholder: { type: String, default: '' },
    buttonLabel: { type: String, default: '' },
    countryPlaceholder: { type: String, default: '' },
    angleDownSolid: { type: [String, Object], default: null },
    imageSrc: { type: [String, Object], default: null },
    vignetteActiveUrl: { type: String, default: '' },
    vignetteNotFoundUrl: { type: String, default: '' },
});

const store = useStore();
const stepData = reactive({
    plate_country: '',
    license_plate: '',
});
const validityHp = ref('');

const validityErrors = computed(
    () => store.getters['validityCheck/errors'] ?? { country: false, plate: false }
);
const submitting = computed(() => store.getters['validityCheck/isVerifying']);

const texts = computed(() => store.getters.texts || {});
const formFields = computed(() => store.getters['form/formFields'] || []);
const countries = computed(() => store.getters['form/countries'] || []);

const vehicleCountryFieldForRender = computed(() => {
    const fromApi = formFields.value.find((f) => f.key === 'plate_country');
    const placeholder =
        props.countryPlaceholder ||
        texts.value['enter-details-1-label'] ||
        texts.value['validity-check.blade.php-7'] ||
        '';

    if (fromApi) {
        return {
            ...fromApi,
            options: countries.value || [],
            translated: {
                ...(fromApi.translated || {}),
                placeholder,
            },
        };
    }

    return {
        key: 'plate_country',
        type: 'country_select',
        translated: { placeholder },
        options: countries.value || [],
    };
});

const licensePlateFieldForRender = computed(() => {
    const fromApi = formFields.value.find((f) => f.key === 'license_plate');
    const placeholder = props.platePlaceholder || fromApi?.translated?.placeholder || '';

    if (fromApi) {
        return {
            ...fromApi,
            translated: {
                ...(fromApi.translated || {}),
                label: '',
                placeholder,
            },
        };
    }

    return {
        key: 'license_plate',
        type: 'text_input',
        translated: {
            label: '',
            placeholder,
        },
    };
});

function countryMeta(plate_country) {
    const code = String(plate_country || '').trim().toUpperCase();
    const fromApi = (countries.value || []).find(
        (c) => String(c?.code || '').trim().toUpperCase() === code
    );
    const plateKey = code;
    const lp = plateKey ? LICENSE_PLATES[plateKey] : null;
    const strip = lp?.code ? String(lp.code) : '';
    const flagIso =
        (plateKey && LICENSE_PLATE_FLAG_ISO2[plateKey]) ||
        (plateKey.length === 2 ? plateKey.toLowerCase() : 'eu');
    const countryLabel =
        fromApi?.translated?.name ||
        fromApi?.name ||
        String(plateKey || '').trim();
    return { plateKey, strip, flagIso, countryLabel };
}

function persistVignetteActiveContext(plate_country, license_plate, apiData) {
    const { plateKey, strip, flagIso, countryLabel } = countryMeta(plate_country);
    const startDate = apiData?.start_date ?? null;
    const endDate = apiData?.end_date ?? null;
    const registry =
        startDate || endDate
            ? {
                  vehicle: { licensePlate: license_plate },
                  charges: [
                      {
                          validSince: startDate,
                          validUntil: endDate,
                          isCurrentlyValid: true,
                      },
                  ],
                  isGivenExemption: false,
                  source: 'orders',
              }
            : null;
    const payload = {
        license_plate,
        country_id: plateKey,
        country_label: countryLabel,
        plate_key: plateKey,
        plate_strip_code: strip.slice(0, 3),
        flag_iso: flagIso,
        verified_at: new Date().toISOString(),
        start_date: startDate,
        end_date: endDate,
        registry,
        edalnice: registry,
    };
    try {
        sessionStorage.setItem(VIGNETTE_ACTIVE_SESSION_KEY, JSON.stringify(payload));
    } catch (_) {
        /* ignore */
    }
}

function persistVignetteNotFoundContext(plate_country, license_plate) {
    const { plateKey, strip, flagIso, countryLabel } = countryMeta(plate_country);
    const payload = {
        license_plate,
        country_id: plateKey,
        country_label: countryLabel,
        plate_key: plateKey,
        plate_strip_code: strip.slice(0, 3),
        flag_iso: flagIso,
        verified_at: new Date().toISOString(),
    };
    try {
        sessionStorage.setItem(VALIDITY_CHECK_SESSION_KEY, JSON.stringify(payload));
    } catch (_) {
        /* ignore */
    }
}

function normalizeLicensePlateForApi(raw) {
    return filterLicensePlateInput(
        String(raw ?? '')
            .normalize('NFKC')
            .replace(/[\u00A0\u2000-\u200B\uFEFF]/g, '')
            .replace(/\s+/g, '')
    ).toUpperCase();
}

/** Same plate resolution as Enter Details (incl. DE/AT segment fields). */
function resolveLicensePlateFromStepData(data) {
    const step = data || {};
    let raw = String(step.license_plate ?? '').trim();
    if (!raw) {
        const segs = step.license_plate_segments;
        if (segs && typeof segs === 'object') {
            const seg1 = String(segs.seg1 ?? '').trim();
            const seg2 = String(segs.seg2 ?? '').trim();
            const seg3 = String(segs.seg3 ?? '').trim();
            const country = String(step.plate_country ?? '').trim().toUpperCase();
            if (country === 'DE') {
                raw = (seg1 ? `${seg1}-` : '') + seg2 + seg3;
            } else if (country === 'AT') {
                raw = (seg1 + seg2 + seg3).toUpperCase();
            }
        }
    }
    return normalizeLicensePlateForApi(raw);
}

function resolveResultPageUrl(raw, fallbackPath) {
    const fallback = fallbackPath.startsWith('/') ? fallbackPath : `/${fallbackPath}`;
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) {
        return new URL(fallback, window.location.origin).href;
    }
    try {
        if (/^https?:\/\//i.test(trimmed)) {
            return trimmed;
        }
        const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
        return new URL(path, window.location.origin).href;
    } catch {
        return new URL(fallback, window.location.origin).href;
    }
}

function onCountryFieldChange() {
    store.dispatch('validityCheck/clearFieldError', 'country');
}

function clearPlateError() {
    store.dispatch('validityCheck/clearFieldError', 'plate');
}

function seedPlateCountryFromQuery() {
    if (typeof window === 'undefined') return;
    const fromQuery = new URLSearchParams(window.location.search).get('plate_country');
    const code = String(fromQuery || '').trim();
    if (code) stepData.plate_country = code;
}

function applyInitialCountry() {
    if (String(stepData.plate_country || '').trim()) return;
    const list = countries.value || [];
    if (!list.length) return;
    const code = resolveDefaultVehicleCountryCode(store.getters.lang?.code, list);
    if (code) stepData.plate_country = code;
}

/** Keep in sync with buy flow after form/loadInitialData + syncVehicleCountryToLanguage. */
function syncStepFromFormStore() {
    const fd = store.getters['form/formData'] || {};
    if (!String(stepData.plate_country || '').trim() && String(fd.plate_country || '').trim()) {
        stepData.plate_country = fd.plate_country;
    }
}

onMounted(() => {
    seedPlateCountryFromQuery();
    syncStepFromFormStore();
    store.dispatch('form/loadInitialData').then(() => {
        syncStepFromFormStore();
        applyInitialCountry();
    }).catch(() => {
        syncStepFromFormStore();
        applyInitialCountry();
    });
});

watch(countries, () => {
    applyInitialCountry();
}, { immediate: true });

async function submitCheck() {
    syncStepFromFormStore();
    applyInitialCountry();

    const plate_country = String(stepData.plate_country ?? '').trim().toUpperCase();
    const license_plate = resolveLicensePlateFromStepData(stepData);
    stepData.license_plate = license_plate;

    const notFoundUrl = resolveResultPageUrl(
        props.vignetteNotFoundUrl,
        '/vignette-not-found/'
    );
    const activeUrl = resolveResultPageUrl(props.vignetteActiveUrl, '/vignette-active/');

    if (validityHp.value) {
        persistVignetteNotFoundContext(plate_country, license_plate);
        await store.dispatch('validityCheck/markNotFound', { plate_country, license_plate });
        window.location.assign(notFoundUrl);
        return;
    }

    const result = await store.dispatch('validityCheck/submitCheck', {
        plate_country,
        license_plate,
    });

    if (result?.kind === 'active') {
        persistVignetteActiveContext(plate_country, license_plate, result.data);
        window.location.assign(activeUrl);
        return;
    }

    if (result?.kind === 'not-found') {
        persistVignetteNotFoundContext(plate_country, license_plate);
        window.location.assign(notFoundUrl);
        return;
    }
}
</script>

<style scoped>
.how-it-works-validity-country :deep(button) {
    height: 3.5rem;
    min-height: 3.5rem;
    align-items: center;
    background-color: rgb(249 250 251);
    border-color: rgb(229 231 235);
    border-radius: 0.75rem;
    padding: 0 1rem;
}

.how-it-works-validity-country :deep(button > div),
.how-it-works-validity-country :deep(button > span),
.how-it-works-validity-country :deep(button > div > span) {
    align-items: center;
    margin-top: 0;
}

.how-it-works-validity-plate :deep(.mb-3) {
    margin-bottom: 0;
}

.how-it-works-validity-plate :deep([class*='ring-1'][class*='rounded']) {
    min-height: 3.5rem;
    align-items: center;
    background-color: rgb(249 250 251);
    border-radius: 0.75rem;
}
</style>
